import type { QueueItem } from '@/types/intake';

/**
 * Client-side sequencing for the resulting queue. The queue arrives in one payload, so re-sorting
 * never re-hits the API. Five keys, each ascending or descending: last name (exact column with a
 * full-name fallback), USPS delivered-to-lab date, return tracking no., kit barcode, member id.
 */

export type SortField = 'lastName' | 'delivery' | 'tracking' | 'barcode' | 'memberId';
export type SortDir = 'asc' | 'desc';

export const SORT_LABELS: Record<SortField, string> = {
  lastName: 'Last name',
  delivery: 'Delivery date',
  tracking: 'Tracking no.',
  barcode: 'Barcode',
  memberId: 'Member ID',
};

export const SORT_FIELDS = Object.keys(SORT_LABELS) as SortField[];

const NAME_SUFFIXES = new Set(['JR', 'SR', 'II', 'III', 'IV', 'V']);

/** Exact last name from the discrete column; falls back to the last meaningful token of the name. */
export function lastNameKey(it: QueueItem): string {
  const ln = (it.memberLastName ?? '').trim();
  if (ln) return ln.toUpperCase();
  const parts = (it.memberName ?? '').trim().toUpperCase().split(/\s+/).filter(Boolean);
  while (parts.length > 1 && NAME_SUFFIXES.has(parts[parts.length - 1]!)) parts.pop();
  return parts.length ? parts[parts.length - 1]! : '';
}

/** Stable-ish ascending comparison for one field. Direction is applied by the caller. */
export function compareQueueItems(a: QueueItem, b: QueueItem, field: SortField): number {
  switch (field) {
    case 'lastName': {
      const byLast = lastNameKey(a).localeCompare(lastNameKey(b));
      if (byLast !== 0) return byLast;
      const fa = (a.memberFirstName ?? a.memberName ?? '').toUpperCase();
      const fb = (b.memberFirstName ?? b.memberName ?? '').toUpperCase();
      return fa.localeCompare(fb);
    }
    case 'delivery':
      return (a.usps.sampleReceivedTs ?? '').localeCompare(b.usps.sampleReceivedTs ?? '');
    case 'tracking':
      return (a.usps.returnTracking ?? '').localeCompare(b.usps.returnTracking ?? '');
    case 'barcode':
      return (a.kitBarcode ?? '').localeCompare(b.kitBarcode ?? '');
    case 'memberId': {
      const na = Number(a.memberId);
      const nb = Number(b.memberId);
      if (Number.isFinite(na) && Number.isFinite(nb)) return na - nb;
      return (a.memberId ?? '').localeCompare(b.memberId ?? '');
    }
  }
}

/** Return a new array sorted by field + direction (does not mutate the input). */
export function sortQueue(items: QueueItem[], field: SortField, dir: SortDir): QueueItem[] {
  const factor = dir === 'asc' ? 1 : -1;
  return [...items].sort((a, b) => factor * compareQueueItems(a, b, field));
}
